/**
 * CountDetails Component
 * 
 * Displays comprehensive statistics for the Data Management section including:
 * - Total counts for ingredients, products, suppliers, and packaging
 * - Product breakdowns showing products with/without ingredients and scores
 * - Environment counts for Guava staging and production
 * 
 * This component fetches data from multiple sources:
 * - Dashboard service for basic counts (suppliers)
 * - Admin service for Guava environment data and product statistics API
 * - Packaging service for packaging counts
 * - Product statistics API for all product-related counts (total products, breakdowns)
 * 
 * The component now uses the /products/statistics/ API endpoint to get accurate
 * product data including total products count and breakdowns instead of manually 
 * calculating from all products or using dashboard data.
 * 
 * The component uses React hooks for state management and Redux for data fetching.
 */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IApplicationState } from "../../redux/reducers";
import adminService from "../../services/adminService";
import dashboardService from "../../services/dashboardService";
import packagingService from "../../services/packagingService";
import ingredientService from "../../services/ingredientService";

interface CountData {
  // Total counts
  totalIngredients: number;
  totalProducts: number;
  totalSuppliers: number;
  totalPackaging: number;
  
  // Product breakdowns
  productsWithIngredients: number;
  productsWithoutIngredients: number;
  productsWithScores: number;
  productsWithoutScores: number;
  
  // Environment counts (Guava)
  guavaStaging: number;
  guavaProduction: number;
}

const CountDetails: React.FC = () => {
  const dispatch = useDispatch();
  const dashboardData = useSelector((state: IApplicationState) => state.dashboard);
  const packagingData = useSelector((state: IApplicationState) => state.packaging);
  const ingredientData = useSelector((state: IApplicationState) => state.ingredient);
  const adminProducts = useSelector((state: IApplicationState) => state.admin?.products?.products);
  const productStatistics = useSelector((state: IApplicationState) => state.admin?.productStatistics);
  const userId = useSelector((state: IApplicationState) => state.user.user_id);
  
  const [counts, setCounts] = useState<CountData>({
    totalIngredients: 0,
    totalProducts: 0,
    totalSuppliers: 0,
    totalPackaging: 0,
    productsWithIngredients: 0,
    productsWithoutIngredients: 0,
    productsWithScores: 0,
    productsWithoutScores: 0,
    guavaStaging: 0,
    guavaProduction: 0,
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAllCounts = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch dashboard overview for basic counts
        await dispatch(dashboardService.getDashboardOverview());
        
        // Fetch ingredients data to get total count from pagination
        try {
          await dispatch(ingredientService.getPaginatedIngredients(false));
        } catch (err) {
          console.warn("Could not fetch ingredients data:", err);
        }
        
        // Fetch packaging data if user is available
        if (userId) {
          try {
            await dispatch(packagingService.getAllPackages(userId));
          } catch (err) {
            console.warn("Could not fetch packaging data:", err);
          }
        }
        
        // Fetch Guava environment counts in parallel
        const [guavaStgResult, guavaProdResult] = await Promise.allSettled([
          dispatch(adminService.getGuavaDataStg()) as any,
          dispatch(adminService.getGuavaData()) as any,
        ]);

        const guavaStg = guavaStgResult.status === 'fulfilled' ? guavaStgResult.value : null;
        const guavaProd = guavaProdResult.status === 'fulfilled' ? guavaProdResult.value : null;

        // Update Guava environment counts
        setCounts(prev => ({
          ...prev,
          guavaStaging: guavaStg?.data?.products ?? 0,
          guavaProduction: guavaProd?.data?.products ?? 0,
        }));

        // Fetch product statistics from the new API
        try {
          await dispatch(adminService.getProductStatistics());
        } catch (err) {
          console.warn("Could not fetch product statistics:", err);
        }

        // Fetch all products to populate Redux store (fallback for other data)
        try {
          await dispatch(adminService.getAdminProducts(true));
        } catch (err) {
          console.warn("Could not fetch products for analysis:", err);
        }
        
      } catch (err) {
        console.error("Failed to fetch counts", err);
        setError("Failed to load count data. Please try refreshing the page.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllCounts();
  }, [dispatch, userId]);

  // Update counts from product statistics API when available
  useEffect(() => {
    if (productStatistics) {
      setCounts(prev => ({
        ...prev,
        totalProducts: productStatistics.total_products,
        productsWithIngredients: productStatistics.products_with_ingredients,
        productsWithoutIngredients: productStatistics.products_without_ingredients,
        productsWithScores: productStatistics.products_with_scores,
        productsWithoutScores: productStatistics.products_without_scores,
      }));
    }
  }, [productStatistics]);

  // Analyze products from Redux store for breakdowns (fallback if API data not available)
  useEffect(() => {
    if (!productStatistics && adminProducts && Array.isArray(adminProducts)) {
      let withIngredients = 0;
      let withoutIngredients = 0;
      let withScores = 0;
      let withoutScores = 0;
      
      adminProducts.forEach((product: any) => {
        // Check if product has ingredients (array with at least one ingredient)
        if (product.ingredients && Array.isArray(product.ingredients) && product.ingredients.length > 0) {
          withIngredients++;
        } else {
          withoutIngredients++;
        }
        
        // Check if product has scores (nutrition_score or sustainability_score)
        const hasNutritionScore = product.nutrition_score !== undefined && product.nutrition_score !== null && product.nutrition_score !== 0;
        const hasSustainabilityScore = product.sustainability_score !== undefined && product.sustainability_score !== null && product.sustainability_score !== 0;
        
        if (hasNutritionScore || hasSustainabilityScore) {
          withScores++;
        } else {
          withoutScores++;
        }
      });

      setCounts(prev => ({
        ...prev,
        productsWithIngredients: withIngredients,
        productsWithoutIngredients: withoutIngredients,
        productsWithScores: withScores,
        productsWithoutScores: withoutScores,
      }));
    }
  }, [adminProducts, productStatistics]);

  // Update counts from dashboard data when available (fallback for non-product counts)
  useEffect(() => {
    if (dashboardData) {
      setCounts(prev => ({
        ...prev,
        // Only use dashboard total_products if statistics API data is not available
        totalProducts: productStatistics?.total_products ?? dashboardData.total_products ?? 0,
        totalSuppliers: dashboardData.manufacturers ?? 0,
      }));
    }
  }, [dashboardData, productStatistics]);

  // Update ingredient count from ingredient data when available
  useEffect(() => {
    if (ingredientData?.ingredients?.pagination?.total !== undefined) {
      setCounts(prev => ({
        ...prev,
        totalIngredients: ingredientData.ingredients.pagination.total
      }));
    }
  }, [ingredientData]);

  // Update packaging count when available
  useEffect(() => {
    if (packagingData?.packaging?.pagination?.total !== undefined) {
      setCounts(prev => ({
        ...prev,
        totalPackaging: packagingData.packaging.pagination.total
      }));
    }
  }, [packagingData]);

  if (loading) {
    return (
      <div className="count-details-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading counts...</span>
        </div>
        <p className="mt-2">Loading count data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <i className="fas fa-exclamation-triangle mr-2"></i>
        {error}
      </div>
    );
  }

  return (
    <div className="count-details-container">
      {/* Main Counts Section */}
      <div className="count-section">
        <h4 className="count-section-title">Overview</h4>
        <div className="count-cards-grid">
          <CountCard
            title="Total Ingredients"
            count={counts.totalIngredients}
            icon="fas fa-leaf"
            color="success"
          />
          <CountCard
            title="Total Products"
            count={counts.totalProducts}
            icon="fas fa-box"
            color="primary"
          />
          <CountCard
            title="Total Suppliers"
            count={counts.totalSuppliers}
            icon="fas fa-truck"
            color="info"
          />
          <CountCard
            title="Total Packaging"
            count={counts.totalPackaging}
            icon="fas fa-cube"
            color="warning"
          />
        </div>
      </div>

      {/* Product Breakdowns Section */}
      <div className="count-section mt-4">
        <h4 className="count-section-title">Product Breakdowns</h4>
        <div className="count-cards-grid">
          <CountCard
            title="Products with Ingredients"
            count={counts.productsWithIngredients}
            icon="fas fa-check-circle"
            color="success"
            subtitle="Have ingredient data"
          />
          <CountCard
            title="Products without Ingredients"
            count={counts.productsWithoutIngredients}
            icon="fas fa-times-circle"
            color="danger"
            subtitle="Missing ingredient data"
          />
          <CountCard
            title="Products with Scores"
            count={counts.productsWithScores}
            icon="fas fa-star"
            color="success"
            subtitle="Have been scored"
          />
          <CountCard
            title="Products without Scores"
            count={counts.productsWithoutScores}
            icon="fas fa-star-half-alt"
            color="secondary"
            subtitle="Not yet scored"
          />
        </div>
      </div>

      {/* Environment Counts Section */}
      <div className="count-section mt-4">
        <h4 className="count-section-title">Environment Counts</h4>
        <div className="count-cards-grid">
          <CountCard
            title="Guava - Staging"
            count={counts.guavaStaging}
            icon="fas fa-flask"
            color="info"
          />
          <CountCard
            title="Guava - Production"
            count={counts.guavaProduction}
            icon="fas fa-rocket"
            color="success"
          />
        </div>
      </div>
    </div>
  );
};

interface CountCardProps {
  title: string;
  count: number;
  icon: string;
  color: "primary" | "success" | "info" | "warning" | "danger" | "secondary";
  subtitle?: string;
}

const CountCard: React.FC<CountCardProps> = ({ title, count, icon, color, subtitle }) => {
  return (
    <div className={`count-card count-card-${color}`}>
      <div className="count-card-header">
        <div className={`count-card-icon bg-${color}`}>
          <i className={icon}></i>
        </div>
        <div className="count-card-info">
          <div className="count-card-title">{title}</div>
          {subtitle && <div className="count-card-subtitle">{subtitle}</div>}
        </div>
      </div>
      <div className="count-card-value">{count.toLocaleString()}</div>
    </div>
  );
};

export default CountDetails;
